import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "@mui/material";

import styles from "./Books.module.scss";

import {
  Pagination,
  Loader,
  BookCard,
  StickySearchBar,
} from "../../library/common";
import {
  FilterSection,
  ModernSlider,
  SearchCountContainer,
} from "../../library/component";
import {
  getBookAll,
  getBookCategories,
  getBookLanguages,
  getBookSeries,
} from "../../services/apis/book";

import localesKeys from "../../locales/localesKeys.json";
import MetaTag from "../../library/meta";
import NoResult from "../../library/common/NoResult/NoResult";

const ITEMS_PER_PAGE = 21;

const Books = ({ path = "granth", type = "book" }) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef();
  const bannerPath = path === "granth" ? "book" : path;

  const initBook = {
    list: [],
    total: 0,
    loader: true,
  };
  const [books, setBooks] = useState(initBook);
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [series, setSeries] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    categories: [],
    languages: [],
    series: [],
    expandes: ["categories", "languages", "series"],
    search: "",
  });

  const handleFilterConfig = (key, value, filtersObj, currentPage) => {
    let newFilters = {
      ...filtersObj,
      [key]: filtersObj?.[key]?.includes(value)
        ? filtersObj?.[key]?.filter((item) => item !== value)
        : [...filtersObj[key], value],
    };
    setFilters(newFilters);
    handleGetBooks(newFilters, currentPage);
  };
  const filterConfig = useMemo(() => {
    const config = [
      {
        label: t(localesKeys.GRANT_LANGUAGES_CATEGORIES),
        options: categories,
        name: "categories",
        onExpand: () => {
          setFilters({
            ...filters,
            expandes: filters?.expandes?.includes("categories")
              ? filters?.expandes?.filter((item) => item !== "categories")
              : [...filters.expandes, "categories"],
          });
        },
        onCheckBoxMarked: (value) => {
          handleFilterConfig("categories", value, filters, currentPage);
        },
      },
      {
        label: t(localesKeys.GRANT_LANGUAGES_LANGUAGE),
        options: languages,
        name: "languages",
        onExpand: () => {
          setFilters({
            ...filters,
            expandes: filters?.expandes?.includes("languages")
              ? filters?.expandes?.filter((item) => item !== "languages")
              : [...filters.expandes, "languages"],
          });
        },
        onCheckBoxMarked: (value) => {
          handleFilterConfig("languages", value, filters, currentPage);
        },
      },
      {
        label: t(localesKeys.GRANT_LANGUAGES_SERIES),
        options: series,
        name: "series",
        onExpand: () => {
          setFilters({
            ...filters,
            expandes: filters?.expandes?.includes("series")
              ? filters?.expandes?.filter((item) => item !== "series")
              : [...filters.expandes, "series"],
          });
        },
        onCheckBoxMarked: (value) => {
          handleFilterConfig("series", value, filters, currentPage);
        },
      },
    ];

    return config;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, languages, series, filters, t]);

  const handleGetBooks = (filters, page) => {
    getBookAll({ filters: filters, page: page, limit: ITEMS_PER_PAGE, type })
      .then((res) => {
        if (res?.success) {
          setBooks({ ...res?.data, loader: false });
        } else {
          setBooks({ ...initBook, loader: false });
        }
      })
      .catch((err) => {
        setBooks({ ...initBook, loader: false });
      });
  };
  useEffect(() => {
    if (!categories?.length) {
      getBookCategories({ type: "isBook" })
        .then((res) => {
          if (res?.success) {
            setCategories(res?.data);
          } else {
            setCategories([]);
          }
        })
        .catch((err) => {
          setCategories([]);
        });
    }
    if (!languages?.length) {
      getBookLanguages()
        .then((res) => {
          if (res?.success) {
            setLanguages(res?.data);
          } else {
            setLanguages([]);
          }
        })
        .catch((err) => {
          setLanguages([]);
        });
    }
    if (!series?.length) {
      getBookSeries()
        .then((res) => {
          if (res?.success) {
            setSeries(res?.data);
          } else {
            setSeries([]);
          }
        })
        .catch((err) => {
          setSeries([]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const defaultFilters = {
      categories: [],
      languages: [],
      series: [],
      expandes: ["categories", "languages", "series"],
      search: "",
    };

    setBooks({
      list: [],
      total: 0,
      loader: true,
    });
    setCurrentPage(1);
    setFilters(defaultFilters);

    handleGetBooks(defaultFilters, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, path]);

  let startPage = Math.min(
    (currentPage - 1) * ITEMS_PER_PAGE + 1,
    books?.total
  );
  let endPage = Math.min(currentPage * ITEMS_PER_PAGE, books?.total);

  return (
    <div ref={scrollContainerRef} className={styles.booksContainer}>
      <MetaTag
        title={t(localesKeys.GRANT_TITLE)}
        shortDescription={t(localesKeys.GRANT_DESCRIPTION)}
      />
      <ModernSlider path={bannerPath} />

      <StickySearchBar
        className={"desktopSearchBar"}
        placeholder={t(localesKeys.SEARCH)}
        onSearch={(value) => {
          let newFilters = { ...filters, search: value };
          setFilters(newFilters);
          handleGetBooks(newFilters, currentPage);
        }}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
      />

      <div className={styles.containerFluid}>
        <Container maxWidth="false" className={styles.container}>
          <div className={`${styles.booksDisplayContainer}`}>
            <FilterSection
              setFilters={setFilters}
              currentPage={currentPage}
              handleGetBooks={handleGetBooks}
              config={filterConfig}
              filters={filters}
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
            />
            <div className={styles.booksListContainer}>
              <SearchCountContainer
                startPage={startPage}
                endPage={endPage}
                total={books?.total}
                onSearch={(value) => {
                  let newFilters = { ...filters, search: value };
                  setFilters(newFilters);
                  handleGetBooks(newFilters, currentPage);
                }}
              />

              {books?.total ? (
                <div className={styles.gridContainer}>
                  {books?.list?.map((book, index) => (
                    <BookCard key={index} book={book} parentPath={path} />
                  ))}
                </div>
              ) : (
                <NoResult />
              )}
              <Pagination
                total={books?.total ? books?.total : 0}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                setCurrentPage={(n) => {
                  setCurrentPage(n);
                  handleGetBooks(filters, n);
                }}
                className={styles.paginationContainer}
              />
            </div>
          </div>
          {books?.loader && <Loader />}
        </Container>
      </div>
    </div>
  );
};

export default Books;
